<template>
    <div class="credentials-details">
      <snack-bar class="error-bar" v-if="showErrorBar" :variant="'danger'" :text="errorBarText"/>
      <div class="page-content">
        <img class="logo-image" :src="require(`@/assets/bank-logos/${source.logoFile}`)">
        <div class="title" v-if="source.type === 'bank'">
        כדי לחבר את חשבון הבנק צריך להזין את <span class="bold">פרטי המשתמש הקבועים</span> באתר או באפליקציה של הבנק:
        </div>
        <div class="title" v-else>
          כדי לחבר את כרטיס {{ source.displayName }} צריך להזין את
          <span class="bold">פרטי המשתמש הקבועים</span>
          שלך לאתר או לאפליקציה של {{ source.displayName }}.
        </div>
        <info-tool-tip
          class="tool-tip-wrapper"
          :text="tooltipText"
          :popup-text="'כאן יש הסבר מדויק.'"
          :event-name="'CredentialsInfoTooltipClicked'"
          :popup-component="credentialsHelpPopup"
          :popup-props="credentialsHelpPopupProps"/>
        <ValidationObserver ref="observer" slim>
        <credentials-input-field class="input-container"
                                 :field-name="'של מי החשבון? (שם)'"
                                 :validations="'min-length:2'"
                                 :error="nameAlreadyUsedError"
                                 :error-message="nameAlreadyUsedErrorMessage"
                                 :field-type="'text'"
                                 :field-value="name"
                                 :update-input="updateName"/>
        <div v-for="(field, fieldKey) in source.fields" :key="fieldKey">
          <credentials-input-field v-if="field.type !== 'password'"
                                   ref="field"
                                   class="input-container"
                                   :field-name="field.name"
                                   :error="invalidCredentials && showError"
                                   :field-value="credentials[fieldKey]"
                                   :update-input="updateCredentials(fieldKey)"
                                   :field-type="field.type"
                                   :validations="field.validation"/>
          <credentials-input-field v-else
                                   ref="field"
                                   class="input-container"
                                   :error="invalidCredentials && showError"
                                   :field-name="field.name"
                                   :field-type="field.type"
                                   :field-value="credentials[fieldKey]"
                                   :validations="field.validation"
                                   :update-input="updateCredentials(fieldKey)"/>
        </div>
        </ValidationObserver>
        <div class="button-wrapper">
          <riseup-button
            class="save-button"
            :disabled="!buttonEnabled"
            :title="buttonTitle"
            :action="saveCredentials"
            :size="'small'"/>
        </div>
      </div>
    </div>
</template>

<script>
import BaseUi from 'base-ui';
import _ from 'lodash';
import CredentialsInputField from './CredentialsInputField.vue';
import InfoToolTip from './InfoToolTip.vue';
import ModalBus from '../../event-bus/modal-bus';
import Segment from '../../Segment';
import BankCredsHelpPopup from './help-popups/BankCredsHelpPopup.vue';
import VisaCalCredsHelpPopup from './help-popups/VisaCalCredsHelpPopup.vue';
import IsracardCredsHelpPopup from './help-popups/IsracardCredsHelpPopup.vue';
import AmexCredsHelpPopup from './help-popups/AmexCredsHelpPopup.vue';
import LeumiCardMaxCredsHelpPopup from './help-popups/LeumiCardMaxCredsHelpPopup.vue';

export default {
  name: 'CredentialsDetails',
  components: {
    CredentialsInputField,
    InfoToolTip,
    RiseupButton: BaseUi.RiseupButton,
    SnackBar: BaseUi.SnackBar,
  },
  props: {
    source: {
      type: Object,
      required: true,
    },
    onSave: {
      type: Function,
      required: true,
    },
    nameAlreadyUsedError: {
      type: Boolean,
      default: false,
    },
    saveCredentialsError: {
      type: Boolean,
      default: false,
    },
    setNameAlreadyInUseError: {
      type: Function,
    },
    invalidCredentials: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      credentials: {},
      name: '',
      showError: false,
    };
  },
  beforeDestroy() {
    this.closeModal();
  },
  computed: {
    credentialsHelpPopup() {
      if (this.source.type === 'bank') {
        return BankCredsHelpPopup;
      }
      // credit card
      switch (this.source.sourceName) {
        case 'ישראכארט':
          return IsracardCredsHelpPopup;
        case 'ויזה כאל':
          return VisaCalCredsHelpPopup;
        case 'לאומי קארד':
          return LeumiCardMaxCredsHelpPopup;
        case 'אמריקן אקספרס':
          return AmexCredsHelpPopup;
        default:
          return null;
      }
    },
    credentialsHelpPopupProps() {
      if (this.source.type === 'bank') {
        return {
          sourceName: this.source.sourceName,
        };
      }
      // credit card
      return null;
    },
    tooltipText() {
      return this.source.type === 'bank' ? 'לא ברור לך מה פרטי המשתמש שלך?' : 'איפה אפשר למצוא את פרטי המשתמש הקבועים שלי?';
    },
    buttonTitle() {
      return this.source.type === 'bank' ? 'חיבור חשבון בנק' : `חיבור לאתר ${this.source.displayName}`;
    },
    loadingText() {
      return this.source.type === 'bank' ? 'הבנק' : 'חברת האשראי';
    },
    credentialsForSave() {
      return _.mapValues(this.credentials, val => val.trim());
    },
    isValidName() {
      return this.name.length > 1;
    },
    nameAlreadyUsedErrorMessage() {
      return this.nameAlreadyUsedError ? 'כבר השתמשת בשם הזה' : null;
    },
    allFieldsHaveInput() {
      return _.every(_.keys(this.source.fields), fieldKey => this.credentials[fieldKey] && this.credentials[fieldKey].length > 3);
    },
    buttonEnabled() {
      return this.isValidName && this.allFieldsHaveInput;
    },
    showErrorBar() {
      return this.showError && (this.saveCredentialsError || this.invalidCredentials);
    },
    errorBarText() {
      if (this.saveCredentialsError) {
        Segment.trackUserGot('CredentialsSaveFailed');
        return 'קרתה תקלה בשמירת הנתונים, אפשר לנסות שוב';
      } if (this.invalidCredentials) {
        Segment.trackUserGot('InvalidCredentials');
        return 'נראה שאחד מפרטי המשתמש לא נכון';
      }
      return '';
    },
  },
  methods: {
    updateCredentials(fieldKey) {
      return value => {
        this.$set(this.credentials, fieldKey, value);
      };
    },
    closeModal() {
      this.setNameAlreadyInUseError(false);
      ModalBus.$emit('close-modal');
    },
    updateName(name) {
      this.name = name;
      this.setNameAlreadyInUseError(false);
    },
    async saveCredentials() {
      this.showError = true;
      Segment.trackUserInteraction('AccountSaveClicked', { sourceName: this.source.sourceName });
      const valid = await this.$refs.observer.validate();
      _.forEach(this.$refs.field, field => field.validate());
      if (valid) {
        await this.onSave({
          sourceName: this.source.sourceName,
          credentials: this.credentialsForSave,
          name: this.name,
          sourceType: this.source.type,
        });
      }
    },
  },
  watch: {
    async showErrorBar(newValue, oldValue) {
      if (newValue) {
        window.scrollTo(0, 0);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '../../../node_modules/base-ui/src/scss/riseup-colors';
  @import '../../scss/mixins';

  .credentials-details {
    width: 100%;

    .error-bar {
      @include responsive-padding;
    }

    .page-content {
      padding: 30px;
      @include responsive-padding;

      .logo-image {
        height: 40px;
        margin-bottom: 20px;
      }

      .title {
        color: $riseup_black;
        font-size: 18px;
        line-height: 1.44;
        margin-bottom: 20px;

        .bold {
          font-weight: bold;
        }
      }

      .tool-tip-wrapper {
        margin-bottom: 35px;
      }

      .input-container {
        margin-bottom: 30px;
      }

      .button-wrapper {
        .save-button {
          width: 100%;
        }
      }
    }
  }

</style>
